import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { CHAT_PRODUCT, ONE_TO_MANY_PRODUCT, ONE_TO_ONE_PRODUCT, ON_DEMAND_PRODUCT } from 'constants/products.js';
import {
  ORG_ONE_TO_MANY_HOME,
  ORG_ONE_TO_ONE_HOME,
  buildRoute,
  ORG_ON_DEMAND_VIDEOS,
  ORG_ON_DEMAND_GET_STARTED,
  PRODUCT_ROUTES,
} from 'constants/routes.js';
import usePermissions from 'hooks/usePermissions.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import SkeletonCell from 'components-v3/List/cells/SkeletonCell.jsx';
import {
  CustomProductSwitcher,
  SkeletonListWrapper,
  SkeletonWrapper,
  SkeletonColumn,
  StyledSkeletonCell,
  SwitchersWrapper,
  SwitchersList,
  SwitchersListItem,
  GroupsWrapper,
  productLabelStyles,
} from './styles.js';
import Labels from 'components/Labels/index.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import OnDemandProductSwitcherTooltip from './OnDemandProductSwitcherTooltip.jsx';

const SOCIAL_COMMERCE = 'social-commerce';
const DIGITAL_CLIENTELING = 'digital-clienteling';

const PRODUCT_LIST_LABELS_BY_GROUP = {
  [SOCIAL_COMMERCE]: 'Social Commerce',
  [DIGITAL_CLIENTELING]: 'Digital Clienteling',
};

const OrgProductList = ({ selectedOrg, currentOrg, selectedProduct, selectingProduct, showSkeleton }) => {
  const history = useHistory();
  const { hasOrgPermission, hasOrgSpecificProductPermission } = usePermissions();
  const { features } = useFeatureContext();

  // If it is the same org or no org selected
  const isSameOrg = selectedOrg?.id === currentOrg?.id || selectedOrg === null;

  const getProductList = useCallback(
    ({ id, features } = {}, isSameOrg) => {
      const products = [];
      if (!features) return products;

      if (features.shows?.enabled) {
        const hasOneToManyPermission = hasOrgSpecificProductPermission(id, ONE_TO_MANY_PRODUCT);
        if (hasOneToManyPermission) {
          products.push({
            id: ONE_TO_MANY_PRODUCT,
            selected: isSameOrg && selectedProduct === ONE_TO_MANY_PRODUCT,
            link: buildRoute(ORG_ONE_TO_MANY_HOME, { orgId: id }),
            orgId: id,
            group: SOCIAL_COMMERCE,
          });
        }
      }

      if (hasOrgSpecificProductPermission(id, ON_DEMAND_PRODUCT) && features.vod?.enabled) {
        products.push({
          id: ON_DEMAND_PRODUCT,
          selected: isSameOrg && selectedProduct === ON_DEMAND_PRODUCT,
          link: buildRoute(ORG_ON_DEMAND_VIDEOS, { orgId: id }),
          orgId: id,
          group: SOCIAL_COMMERCE,
        });
      }
      if (features.calls?.enabled) {
        const hasOneToOnePermission = hasOrgSpecificProductPermission(id, ONE_TO_ONE_PRODUCT);
        if (hasOneToOnePermission) {
          products.push({
            id: ONE_TO_ONE_PRODUCT,
            selected: isSameOrg && selectedProduct === ONE_TO_ONE_PRODUCT,
            link: buildRoute(ORG_ONE_TO_ONE_HOME, { orgId: id }),
            orgId: id,
            group: DIGITAL_CLIENTELING,
          });
        }
      }
      if (hasOrgSpecificProductPermission(id, CHAT_PRODUCT) && features.chat?.enabled) {
        products.push({
          id: CHAT_PRODUCT,
          selected: isSameOrg && selectedProduct === CHAT_PRODUCT,
          link: buildRoute(PRODUCT_ROUTES.ORG_CHAT_HOME, { orgId: id, selectedProduct: CHAT_PRODUCT }),
          orgId: id,
          group: DIGITAL_CLIENTELING,
        });
      }
      return products;
    },
    [hasOrgSpecificProductPermission, selectedProduct, features],
  );

  const productList = useMemo(() => {
    if (isSameOrg) return getProductList(currentOrg, isSameOrg);
    if (selectedOrg) return getProductList(selectedOrg);
    return [];
  }, [isSameOrg, getProductList, currentOrg, selectedOrg]);

  const handleProductSelect = useCallback(
    (product) => {
      const isSameProduct = isSameOrg && selectedProduct === product?.id;
      trackingService.track({
        eventType: `select-product:${isSameProduct ? 'same' : product?.id}`,
        previousSelectedProduct: selectedProduct,
      });
      if (isSameProduct) return;
      selectingProduct();
      if (!product?.link) return;
      history.push(product?.link);
    },
    [isSameOrg, selectedProduct, selectingProduct, history],
  );

  const goToGetStarted = (product) => {
    const newLink = buildRoute(ORG_ON_DEMAND_GET_STARTED, { orgId: product?.orgId });
    handleProductSelect({ ...product, link: newLink });
  };

  useEffect(() => {
    if (isSameOrg) return;
    // Select the first product in the list when the org is changed
    const productList = getProductList(selectedOrg);
    const _selectedProduct = productList.find((product) => product.id === selectedProduct);
    if (_selectedProduct) {
      // If the new workspace has the same product type as the selected product, switch to the same product
      handleProductSelect(_selectedProduct);
    } else {
      // If the current selected product does not exist in the productList, select the first product in the list
      handleProductSelect(productList[0]);
    }
  }, [selectedOrg, isSameOrg, selectingProduct, selectedProduct, getProductList, handleProductSelect]);

  const groupedProductList = productList.reduce((groups, product) => {
    if (!groups[product.group]) groups[product.group] = [];

    groups[product.group].push(product);
    return groups;
  }, {});

  if (showSkeleton) {
    return (
      <SkeletonListWrapper layout>
        {productList.map((product) => (
          <SkeletonWrapper key={`skeleton-wrapper-for-${product?.id}`}>
            <SkeletonCell width={44} height={44} borderRadius={6} />
            <SkeletonColumn>
              <StyledSkeletonCell width={146} height={16} borderRadius={16} />
              <StyledSkeletonCell width={96} height={12} borderRadius={12} />
            </SkeletonColumn>
          </SkeletonWrapper>
        ))}
      </SkeletonListWrapper>
    );
  }

  return (
    <GroupsWrapper layout>
      {Object.entries(groupedProductList).map(([key, products]) => (
        <SwitchersWrapper key={key}>
          <Labels
            label={PRODUCT_LIST_LABELS_BY_GROUP[key]}
            labelVariation="roman"
            externalLabelsClassName={productLabelStyles}
          />
          <SwitchersList>
            {products.map((product) => (
              <SwitchersListItem key={product?.id}>
                {product?.id !== ON_DEMAND_PRODUCT ? (
                  <CustomProductSwitcher
                    isSelected={product?.selected ?? false}
                    product={product?.id}
                    onSelect={() => handleProductSelect(product)}
                  />
                ) : (
                  <OnDemandProductSwitcherTooltip
                    selectedProduct={selectedProduct}
                    goTo={() => goToGetStarted(product)}
                    closeProductHeader={() => handleProductSelect(selectedProduct)}
                  >
                    <CustomProductSwitcher
                      isSelected={product?.selected ?? false}
                      product={product?.id}
                      onSelect={() => handleProductSelect(product)}
                    />
                  </OnDemandProductSwitcherTooltip>
                )}
              </SwitchersListItem>
            ))}
          </SwitchersList>
        </SwitchersWrapper>
      ))}
    </GroupsWrapper>
  );
};

OrgProductList.propTypes = {
  selectedOrg: PropTypes.object,
  currentOrg: PropTypes.object,
  selectedProduct: PropTypes.string,
  selectingProduct: PropTypes.func.isRequired,
};

export default OrgProductList;
